import { Component, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { InputDialogData, InputDialogReturnData } from './input.dialog.component';
import { UiService } from './../../services/ui.service';
import { ProgramService } from 'src/app/services/program.service';
import { DropdownOption, Session } from 'globals/store/programs.data';
import { importSheet } from '../../modules/performers/performerSharedFunctions';
import { FuzzyImportDialogComponent, FuzzyImportDialogData } from './fuzzy-import.dialog.component';
import { openDialogGetResult } from '../../helpers/angular-helpers';
import { InputDialogBodyComponent } from './input.dialog.body.component';
import { sleepForMS } from '../../helpers/helpers';
import { CommonModule } from '@angular/common';
import { DropdownComponent } from '../misc/dropdown.component';

@Component({
    selector: 'new-program-dialog',
    template: `
<input-dialog-body
  [data]="newProgramDialogData"
  [dialogRef]="dialogRef"
  [manualSubmit]="true"
  (onMidRight)="addDetail()"
  (onSubmit)="submit()"
  >
  <div class="aboveInput">
    <div class="name">Give your {{ uiService.programTerm.text }} a nickname</div>
  </div>
  <div class="belowInput">
    <em class="tip">Eg. Plenary, Awards Dinner, Training Room, Breakout, Exhibition Hall, Reception Area, Main Stage etc.</em>
    <div class="inherit" *ngIf="this.inheritables.length > 1">
      <div class="instr">Inherit date and venue details from:</div>
      <dropdown [selections]="inheritables" [selected]="inherit_from" null_text="Untitled program..." (select)="updateProgram($event)"></dropdown>
    </div>
    <div class="imported" *ngIf="!!importedSessions?.length">
      {{ importedSessions.length }} Session{{ importedSessions.length > 1 ? 's' : '' }} Will Be Imported
    </div>
    <div class="add-detail" *ngIf="!importedSessions?.length" (click)="importProgram()">+ IMPORT SESSIONS</div>
  </div>
</input-dialog-body>
  `,
    styles: [`
    .aboveInput { display: flex; margin-bottom: 17px; flex-direction: column; }
    .tip { font-size: 12px; color: rgba(0,0,0,0.4); }
    .belowInput { margin-top: -20px; }
    .inherit { margin-top: 20px; }
    .inherit .instr { margin-bottom: 5px; }
    .add-detail { color: #11b8c1; font-size: 11px; margin-top: 20px; font-weight: bold; cursor: pointer; }
    ::ng-deep new-program-dialog dialog-wrapper .title { font-size: 16px !important; font-weight: bold; }
    .imported {     margin-top: 10px; font-weight: bold; }
  `],
    standalone: true,
    imports: [CommonModule, InputDialogBodyComponent, DropdownComponent]
})
export class NewProgramDialogComponent {
  newProgramDialogData: InputDialogData = getNewProgramDialogData(this);
  inheritables: DropdownOption[]
  inherit_from: DropdownOption
  none_title = '-- none --';
  importedSessions: Partial<Session>[]
  @ViewChild(InputDialogBodyComponent) inputDialogBody: InputDialogBodyComponent


  constructor(
    public dialogRef: MatDialogRef<NewProgramDialogComponent>,
    private dialog: MatDialog,
    public uiService: UiService,
    public programService: ProgramService,
  ) { 
    this.inheritables = this.programService.programs.filter(p=>p.date || p.location || p.venueId).map(({ title, id }) => ({ title, id }))
    this.inheritables.unshift({id:null, title:this.none_title})
    this.newProgramDialogData.backgroundColor = this.uiService.labelsByType.program.color;
    if(this.inheritables.length === 0) return;
    const { title, id } = this.inheritables[this.inheritables.length - 1];
    this.inherit_from = { title, id };
    this.newProgramDialogData.secondValue = id;
  }

  addDetail() {
    const secondValue = this.inherit_from ? this.inherit_from.id : null;
    const returnData: InputDialogReturnData = { type: 'AlternativeButton', values: [this.newProgramDialogData.value, secondValue], importedSessions: this.importedSessions } 
    this.dialogRef.close(returnData)
  }

  submit() {
    const secondValue = this.inherit_from ? this.inherit_from.id : null;
    const returnData: InputDialogReturnData = { type: 'MainButton', values: [this.newProgramDialogData.value, secondValue], importedSessions: this.importedSessions } 
    this.dialogRef.close(returnData)
  }

  async importProgram() {
    console.log("SHOULD BE IMPORTING!!!")
    const sheet = await importSheet()
    if (!sheet) return

    const data: FuzzyImportDialogData<Session> = {
      dialogTitle: 'Import sessions into a new program',
      columns: [
        { id: 'title', title: 'Session Title', searchTerms: ['title', 'name', 'session'] },
        { id: 'description', title: 'Session Description', searchTerms: ['about', 'summary', 'description'] },
        { id: 'duration', title: 'Session Duration', searchTerms: ['time', 'duration', 'length', 'minutes'] }
      ],
      sheet,
      transformers: {
        'duration': (n: string) => !n || !Number.isInteger(parseInt(n)) ? 30 : parseInt(n),
        'title': (t: string) => t || '',
        'description': (d: string) => d || '',
      }
    }
    const result = await openDialogGetResult<Partial<Session>[]>(this.dialog, FuzzyImportDialogComponent, {panelClass:'full-screen-modal', data})
    if (!result) return
    console.log(result)
    this.importedSessions = result
    this.inputDialogBody.dialogWrapper.fitChildrenWithDelay(300, 80)
  }

  updateProgram(program){
    const {title,id} = program;
    this.inherit_from = {title, id};
    this.newProgramDialogData.secondValue = id;
  }

}

const getNewProgramDialogData = (self): InputDialogData => ({
  title: 'New '+self.uiService.programTerm.btn,
  value: '',
  placeholder: (self.uiService.programTerm.text.charAt(0).toUpperCase() + self.uiService.programTerm.text.slice(1) + ' nickname'),
  successLabel: 'ADD '+self.uiService.programTerm.btn,
  backgroundColor: null, // since the colors map is attached to instances of ui service, can't access yet
  emptyOK: true,  
  midRightLabel: '+ ADD MORE DETAIL',
})